import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useDealDROChangesActions } from 'datastage/store/dealDROChanges/useDealDROChanges';
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';

// Minimum time to show the loading spinner (in ms) for consistent UX
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates Deal DRO Changes data fetching logic.
 * Handles loading states with minimum duration, error toasts, and Redux updates.
 *
 * This pattern ensures:
 * - Consistent loading spinner behavior (minimum 400ms display)
 * - Centralized error handling
 * - Reusable fetch logic across components
 *
 * @returns {Object} Object containing fetchDealDROChanges function and state values
 */
export function useDealDROChangesData() {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();
    const { updateDealDROChanges } = useDealDROChangesActions();
    const { filterForm, rangeParam, sortParam, itemsPerPage } = useSelector(
        (s) => s.dealDROChanges,
    );

    /**
     * Fetches Deal DRO Changes from the API with the given range.
     * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
     *
     * @param {Array} range - The pagination range array (e.g., [0, 24])
     * @param {Object|null} customFilterData - Optional filter data to override Redux filterForm
     * @param {Array|null} customSort - Optional sort array to override Redux sortParam
     * @returns {Promise<Object|null>} The API response data or null on error
     */
    const fetchDealDROChanges = useCallback(
        async (range, customFilterData = null, customSort = null) => {
            const startTime = Date.now();
            setLoadingTrue();

            const filterData = customFilterData ?? filterForm;
            const sortData = customSort ?? sortParam;

            try {
                const data = await datastageApi.dealDROChanges(
                    filterCriteria(filterData, '', 'dealDROChanges'),
                    range,
                    sortData,
                );

                if (data?.code) {
                    showErrorToast(data?.message || 'Failed to fetch deal DRO changes.');
                    return null;
                }

                return data;
            } catch (error) {
                showErrorToast(error?.message || 'Error fetching deal DRO changes.');
                console.error('Error fetching deal DRO changes:', error);
                return null;
            } finally {
                // Ensure minimum loading duration for consistent UX
                const elapsed = Date.now() - startTime;
                const remainingTime = MIN_LOADING_DURATION - elapsed;

                if (remainingTime > 0) {
                    setTimeout(() => setLoadingFalse(), remainingTime);
                } else {
                    setLoadingFalse();
                }
            }
        },
        [filterForm, sortParam, setLoadingTrue, setLoadingFalse, showErrorToast],
    );

    return {
        fetchDealDROChanges,
        updateDealDROChanges,
        itemsPerPage,
        rangeParam,
        filterForm,
        sortParam,
    };
}
